import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { CaretRight, MapPin } from '@phosphor-icons/react'
import { useFormat, useI18n } from '@/i18n'
import { cn } from '@/lib/cn'
import {
  Badge,
  Banner,
  BilingualLabel,
  Card,
  CardBody,
  CardHeader,
  KeyValue,
  ScanField,
} from '@/ui'
import {
  availability,
  bagsAt,
  declarationInfo,
  placePath,
  type BagView,
  type StoreState,
} from '../projection'
import { classifyScan } from '../rules'
import { BagBadges, BagLine } from '../components/bits'
import { useRefusalText } from '../components/useRefusalText'
import { DeclarationBadge } from '../components/DeclarationBadge'
import { StoreScreen } from '../components/StoreScreen'
import { useNow } from '../useStore'

type Tab = 'places' | 'lot' | 'trips' | 'label'

/**
 * F5.6 on the phone — stock on hand worked out from scans (cache + this phone's own commands):
 * browse by place, look a lot up by scanning one of its tags, trips on the road, and the label
 * check (F5.3-f). Holds and availability come from the cache and sync results.
 */
export function StockPage() {
  return (
    <StoreScreen title="store.stock">
      {(state) => <Stock state={state} />}
    </StoreScreen>
  )
}

function Stock({ state }: { state: StoreState }) {
  const { t } = useI18n()
  const [tab, setTab] = useState<Tab>('places')
  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'places', label: t('store.stockTab.places') },
    { id: 'lot', label: t('store.stockTab.lot') },
    { id: 'trips', label: t('store.stockTab.trips') },
    { id: 'label', label: t('store.stockTab.label') },
  ]
  return (
    <div className="flex flex-col gap-4">
      <div
        role="tablist"
        className="grid grid-cols-4 gap-1 rounded-chip border border-rule bg-paper-sunk p-1"
      >
        {tabs.map((x) => (
          <button
            key={x.id}
            role="tab"
            type="button"
            aria-selected={tab === x.id}
            onClick={() => setTab(x.id)}
            className={cn(
              'min-h-touch rounded-chip px-1 text-[13px] font-semibold',
              tab === x.id ? 'bg-ink text-paper' : 'text-ink',
            )}
          >
            {x.label}
          </button>
        ))}
      </div>
      {tab === 'places' && <Places state={state} />}
      {tab === 'lot' && <LotLookup state={state} />}
      {tab === 'trips' && <Trips state={state} />}
      {tab === 'label' && <LabelCheck state={state} />}
    </div>
  )
}

interface LotRow {
  unit_id: string
  bags: BagView[]
  kgNow: number
  kgGate: number
}

function lotRows(bags: BagView[]): LotRow[] {
  const m = new Map<string, LotRow>()
  for (const b of bags) {
    const r = m.get(b.unit_id) ?? { unit_id: b.unit_id, bags: [], kgNow: 0, kgGate: 0 }
    r.bags.push(b)
    r.kgNow += b.net_g_current ?? b.net_g_gate ?? 0
    r.kgGate += b.net_g_gate ?? 0
    m.set(b.unit_id, r)
  }
  return [...m.values()]
}

function LotRows({ state, rows, now }: { state: StoreState; rows: LotRow[]; now: number }) {
  const { t } = useI18n()
  const fmt = useFormat()
  return (
    <ul>
      {rows.map((r) => {
        const lot = state.lots.get(r.unit_id)
        const first = r.bags[0]!
        const days = first.in_since
          ? Math.max(0, Math.floor((now - new Date(first.in_since).getTime()) / 86_400_000))
          : null
        return (
          <li
            key={r.unit_id}
            className="flex flex-col gap-1 border-b border-rule/70 py-2.5 last:border-b-0"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[15px] font-medium">{lot?.code ?? '—'}</span>
              <span className="font-mono text-[14px]">
                {t('store.nOfM', { n: r.bags.length, m: lot?.bag_count ?? r.bags.length })}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted">
              <span>{t('store.kgNowShort', { kg: fmt.weight(r.kgNow) })}</span>
              <span>{t('store.kgGateShort', { kg: fmt.weight(r.kgGate) })}</span>
              {days !== null && <span>{t('store.daysHere', { days })}</span>}
              <BagBadges state={state} bag={first} now={now} />
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function Places({ state }: { state: StoreState }) {
  const { t } = useI18n()
  const now = useNow()
  const [parent, setParent] = useState<string>(state.siteId)
  const here = state.locations.get(parent)
  const children = [...state.locations.values()].filter((l) => l.parent_id === parent && l.active)
  const bagsHere = useMemo(() => bagsAt(state, parent, false), [state, parent])
  const all = useMemo(() => bagsAt(state, parent), [state, parent])
  const available = all.filter((b) => availability(state, b, now) === 'AVAILABLE').length
  const held = all.filter((b) => availability(state, b, now) === 'ON_HOLD').length
  return (
    <div className="flex flex-col gap-3">
      {here && (
        <Card>
          <CardBody className="flex flex-col gap-2 py-3">
            <div className="flex items-center gap-2 text-[15px]">
              <MapPin className="text-amber-dark" aria-hidden="true" />
              {placePath(state, parent)}
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Badge>{t('store.bagsN', { n: all.length })}</Badge>
              <Badge tone="ok">{t('store.availableN', { n: available })}</Badge>
              {held > 0 && <Badge tone="warn">{t('store.heldN', { n: held })}</Badge>}
              {(here.level === 'BAY' || here.level === 'POSITION') && (
                <DeclarationBadge info={declarationInfo(state, here.id, now)} />
              )}
            </div>
            <div className="flex flex-wrap gap-3 pt-1">
              {here.parent_id && (
                <button
                  type="button"
                  className="text-[13px] underline"
                  onClick={() => setParent(here.parent_id!)}
                >
                  {t('store.up')}
                </button>
              )}
              {here.level !== 'SITE' && (
                <Link
                  className="text-[13px] underline"
                  to={`/m/store/count?location=${encodeURIComponent(here.id)}`}
                >
                  {t('store.count')}
                </Link>
              )}
            </div>
          </CardBody>
        </Card>
      )}
      {children.length > 0 && (
        <Card>
          <CardBody className="py-1">
            <ul>
              {children.map((c) => {
                const n = bagsAt(state, c.id).length
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setParent(c.id)}
                      className="flex min-h-touch w-full items-center justify-between gap-2 border-b border-rule/70 py-2 text-left last:border-b-0"
                    >
                      <span>
                        <span className="block text-[15px]">{c.name ?? c.code}</span>
                        <span className="block font-mono text-[12px] text-muted">{c.code}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        {c.role && c.role !== 'STORAGE' && (
                          <Badge tone="accent">{t(`store.role.${c.role}` as const)}</Badge>
                        )}
                        <span className="font-mono">{n}</span>
                        <CaretRight aria-hidden="true" />
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </CardBody>
        </Card>
      )}
      {bagsHere.length > 0 && (
        <Card>
          <CardHeader
            eyebrow={here?.level === 'SITE' ? t('store.onDockShort') : t('store.lotsHere')}
          />
          <CardBody className="py-1">
            <LotRows state={state} rows={lotRows(bagsHere)} now={now} />
          </CardBody>
        </Card>
      )}
    </div>
  )
}

function LotLookup({ state }: { state: StoreState }) {
  const { t } = useI18n()
  const now = useNow()
  const refusal = useRefusalText()
  const [code, setCode] = useState<string | null>(null)
  const k = code ? classifyScan(state, code) : null
  const unitId =
    k?.kind === 'BAG' ? k.bag.unit_id : k?.kind === 'SEAL' ? (k.sample.unit_id ?? null) : null
  const lot = unitId ? state.lots.get(unitId) : undefined
  const bags = unitId
    ? [...state.bags.values()].filter((b) => b.unit_id === unitId && b.status !== 'VOID')
    : []
  const byPlace = new Map<string, BagView[]>()
  for (const b of bags) {
    const key = b.shipment_id
      ? `trip:${b.shipment_id}`
      : b.status === 'MISSING'
        ? 'missing'
        : `loc:${b.location_id ?? ''}`
    byPlace.set(key, [...(byPlace.get(key) ?? []), b])
  }
  const placeName = (key: string) => {
    if (key === 'missing') return t('store.lotMissing')
    if (key.startsWith('trip:')) {
      const trip = state.trips.get(key.slice(5))
      return t('store.onTrip', { code: trip?.code ?? key.slice(5) })
    }
    return placePath(state, key.slice(4)) || t('store.elsewhere')
  }
  return (
    <div className="flex flex-col gap-3">
      <ScanField
        value={code}
        onChange={(c) => setCode(c)}
        validate={(c) => {
          const kk = classifyScan(state, c)
          if (kk.kind === 'DEAD') return refusal(kk.status === 'VOID' ? 'TAG_VOID' : 'TAG_RETIRED')
          if (kk.kind === 'UNKNOWN') return refusal('TAG_UNKNOWN')
          if (kk.kind === 'LOCATION') return refusal('TAG_WRONG_KIND')
          return null
        }}
        placeholder={<BilingualLabel k="store.lotLookupHint" />}
      />
      {lot && (
        <>
          <Card>
            <CardBody className="py-2">
              <KeyValue
                items={[
                  { label: t('store.lot'), value: lot.code, mono: true },
                  { label: t('store.crop'), value: lot.crop_name ?? lot.crop_id },
                  {
                    label: t('store.stream'),
                    value: lot.stream === 'CONV' ? t('store.badge.ordinary') : t('store.brand'),
                  },
                  {
                    label: t('store.hold'),
                    value:
                      lot.hold === 'NONE'
                        ? t('store.holdNone')
                        : `${lot.hold}${lot.hold_reason ? ` · ${lot.hold_reason}` : ''}`,
                  },
                  {
                    label: t('store.bags'),
                    value: t('store.nOfM', { n: bags.length, m: lot.bag_count }),
                    mono: true,
                  },
                ]}
              />
            </CardBody>
          </Card>
          {lot.hold !== 'NONE' && <Banner tone="warn">{t('store.holdExplain')}</Banner>}
          {[...byPlace.entries()].map(([key, list]) => (
            <Card key={key}>
              <CardHeader eyebrow={`${placeName(key)} · ${list.length}`} />
              <CardBody className="py-1">
                <ul>
                  {list.map((b) => (
                    <BagLine key={b.container_id} state={state} bag={b} now={now} />
                  ))}
                </ul>
              </CardBody>
            </Card>
          ))}
        </>
      )}
      {code && !lot && <Banner tone="info">{t('store.lotNotHere')}</Banner>}
    </div>
  )
}

function Trips({ state }: { state: StoreState }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const trips = [...state.trips.values()].filter(
    (x) => x.status === 'IN_TRANSIT' || x.status === 'LOADING',
  )
  if (!trips.length) return <Banner tone="info">{t('store.noTrips')}</Banner>
  return (
    <div className="flex flex-col gap-2">
      {trips.map((trip) => {
        const bags = trip.items.filter((i) => i.container_id).length
        const kg = trip.items.reduce(
          (s, i) => s + ((i.container_id && state.bags.get(i.container_id)?.net_g_gate) || 0),
          0,
        )
        const incoming = trip.to_location_id === state.siteId
        return (
          <Card key={trip.shipment_id}>
            <CardBody className="flex flex-col gap-1 py-3">
              <div className="flex items-center justify-between">
                <span className="font-mono font-bold">{trip.vehicle_no}</span>
                <Badge tone="info">{trip.code}</Badge>
              </div>
              <span className="text-[13px] text-muted">
                {incoming
                  ? t('store.tripFrom', { from: trip.from_name ?? '—' })
                  : t('store.tripTo', { to: trip.to_name ?? '—' })}
                {' · '}
                {t('store.bagsN', { n: bags })} · {fmt.weight(kg)}
              </span>
              {trip.local && <Badge tone="info">{t('store.badge.notSynced')}</Badge>}
            </CardBody>
          </Card>
        )
      })}
    </div>
  )
}

/** F5.3-f: scan a newly stuck label and confirm the place name it shows. */
function LabelCheck({ state }: { state: StoreState }) {
  const { t } = useI18n()
  const now = useNow()
  const refusal = useRefusalText()
  const [code, setCode] = useState<string | null>(null)
  const k = code ? classifyScan(state, code) : null
  const loc = k?.kind === 'LOCATION' ? k.location : null
  return (
    <div className="flex flex-col gap-3">
      <ScanField
        value={code}
        onChange={(c) => setCode(c)}
        validate={(c) =>
          classifyScan(state, c).kind === 'LOCATION' ? null : refusal('TAG_WRONG_KIND')
        }
        placeholder={<BilingualLabel k="store.labelCheckHint" />}
      />
      {loc && (
        <Card>
          <CardBody className="flex flex-col gap-2 py-3">
            <div className="text-[20px] font-semibold">{placePath(state, loc.id)}</div>
            <KeyValue
              items={[
                { label: t('store.code'), value: loc.code, mono: true },
                { label: t('store.level'), value: loc.level },
                ...(loc.role
                  ? [{ label: t('store.use'), value: t(`store.role.${loc.role}` as const) }]
                  : []),
                { label: t('store.active'), value: loc.active ? t('store.yes') : t('store.no') },
              ]}
            />
            {(loc.level === 'BAY' || loc.level === 'POSITION') && (
              <DeclarationBadge info={declarationInfo(state, loc.id, now)} />
            )}
            <p className="text-[13px] text-muted">{t('store.labelCheckConfirm')}</p>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
